/**
 * TaxiEditorOverlay — draws the editable taxi network (nodes + edges) of the
 * draft plan. Display-only: all interaction is handled by
 * `useTaxiEditorInteraction` via map events, so this SVG stays pointer-events
 * -none (no event tug-of-war with the map). Mirrors FieldGraphDebugOverlay's
 * imperative projection: scene feet → lng/lat (CoordinateService) → screen
 * (map.project) on every camera move, no per-frame React re-render.
 *
 * Elements inactive at `currentTime` (group/window/closure-gated, or
 * emergency-closed) render dimmed + dashed so the operator sees what is live.
 */
import { useEffect, useMemo, useRef } from 'react'
import { CoordinateService } from '@/services/3d/CoordinateService'
import { isActiveAt, type NodeRole, type EdgeRole, type TaxiPlan } from '../data/taxiGraph'

interface Props {
  map: mapboxgl.Map | null
  /** scene origin [lng, lat] */
  center: [number, number] | null
  plan: TaxiPlan | null
  currentTime: number
  selectedNodeId: string | null
  selectedEdgeId: string | null
  selectedRunwayId: string | null
  edgeAnchorNodeId: string | null
  runwayAnchorPos: [number, number] | null
  visible: boolean
}

const NODE_COLORS: Record<NodeRole, string> = {
  taxi: '#94a3b8',
  apron: '#a3e635',
  hold_short: '#fca5a5',
  runway_exit: '#f87171',
  exit_to_taxiway: '#fb923c',
  gate: '#34d399',
}
const EDGE_COLORS: Record<EdgeRole, string> = {
  taxiway: '#34d399',
  apron_lead: '#22d3ee',
  runway: '#ffffff',
  exit: '#fbbf24',
  temporary: '#a78bfa',
}

interface EdgeShape {
  id: string
  a: [number, number]
  b: [number, number]
  color: string
  active: boolean
  selected: boolean
  temporary: boolean
}
interface NodeShape {
  id: string
  pos: [number, number]
  color: string
  active: boolean
  selected: boolean
  anchor: boolean
  isExit: boolean
}
interface RunwayLineShape {
  id: string
  a: [number, number]
  b: [number, number]
  active: boolean
  selected: boolean
}
interface RunwayHandleShape {
  key: string
  pos: [number, number]
  selected: boolean
}
interface RunwayArrowShape {
  key: string
  a: [number, number]
  b: [number, number]
  color: string
}
interface RunwayLabelShape {
  key: string
  pos: [number, number]
  text: string
}

const norm2 = (a: readonly [number, number], b: readonly [number, number]): [number, number] => {
  const dx = b[0] - a[0]
  const dz = b[1] - a[1]
  const len = Math.hypot(dx, dz)
  return len < 1e-6 ? [0, 0] : [dx / len, dz / len]
}

export function TaxiEditorOverlay({
  map,
  center,
  plan,
  currentTime,
  selectedNodeId,
  selectedEdgeId,
  selectedRunwayId,
  edgeAnchorNodeId,
  runwayAnchorPos,
  visible,
}: Props) {
  const { nodes, edges, rwyLines, rwyHandles, rwyArrows, rwyLabels } = useMemo(() => {
    const nodes: NodeShape[] = []
    const edges: EdgeShape[] = []
    const rwyLines: RunwayLineShape[] = []
    const rwyHandles: RunwayHandleShape[] = []
    const rwyArrows: RunwayArrowShape[] = []
    const rwyLabels: RunwayLabelShape[] = []
    if (!plan) return { nodes, edges, rwyLines, rwyHandles, rwyArrows, rwyLabels }

    const groupActive = new Map(
      plan.groups.map((g) => [g.id, isActiveAt(g.active, undefined, currentTime)])
    )
    const nodeById = new Map(plan.nodes.map((n) => [n.id, n]))

    for (const n of plan.nodes) {
      const active = (groupActive.get(n.groupId) ?? true) && isActiveAt(n.active, undefined, currentTime)
      nodes.push({
        id: n.id,
        pos: n.pos,
        color: NODE_COLORS[n.role],
        active,
        selected: n.id === selectedNodeId,
        anchor: n.id === edgeAnchorNodeId,
        isExit: n.role === 'exit_to_taxiway',
      })
    }
    for (const e of plan.edges) {
      const a = nodeById.get(e.a)
      const b = nodeById.get(e.b)
      if (!a || !b) continue
      const active =
        (groupActive.get(e.groupId) ?? true) && isActiveAt(e.active, e.closures, currentTime)
      edges.push({
        id: e.id,
        a: a.pos,
        b: b.pos,
        color: EDGE_COLORS[e.role],
        active,
        selected: e.id === selectedEdgeId,
        temporary: e.role === 'temporary',
      })
    }

    // Runways: centerline + endpoint handles + designators + use-direction arrows.
    for (const r of plan.runways ?? []) {
      const active = (groupActive.get(r.groupId) ?? true) && isActiveAt(r.active, undefined, currentTime)
      const selected = r.id === selectedRunwayId
      const [e0, e1] = r.ends
      rwyLines.push({ id: r.id, a: e0.threshold, b: e1.threshold, active, selected })
      for (const idx of [0, 1] as const) {
        const end = r.ends[idx]
        rwyHandles.push({ key: `${r.id}-${idx}`, pos: end.threshold, selected })
        rwyLabels.push({ key: `${r.id}-${idx}`, pos: end.threshold, text: end.designator })
        // Arrow along the runway showing travel direction at an in-use end:
        // green where aircraft land, amber where they take off.
        const other = r.ends[idx === 0 ? 1 : 0]
        const dir = norm2(end.threshold, other.threshold)
        const tip: [number, number] = [end.threshold[0] + dir[0] * 280, end.threshold[1] + dir[1] * 280]
        if (end.useForLanding) rwyArrows.push({ key: `${r.id}-${idx}-land`, a: end.threshold, b: tip, color: '#34d399' })
        if (end.useForTakeoff) rwyArrows.push({ key: `${r.id}-${idx}-tko`, a: end.threshold, b: tip, color: '#fbbf24' })
      }
    }
    return { nodes, edges, rwyLines, rwyHandles, rwyArrows, rwyLabels }
  }, [plan, currentTime, selectedNodeId, selectedEdgeId, selectedRunwayId, edgeAnchorNodeId])

  const edgeRefs = useRef<Map<string, SVGLineElement | null>>(new Map())
  const nodeRefs = useRef<Map<string, SVGCircleElement | null>>(new Map())
  const rwyLineRefs = useRef<Map<string, SVGLineElement | null>>(new Map())
  const rwyHandleRefs = useRef<Map<string, SVGCircleElement | null>>(new Map())
  const rwyArrowRefs = useRef<Map<string, SVGLineElement | null>>(new Map())
  const rwyLabelRefs = useRef<Map<string, SVGTextElement | null>>(new Map())
  const anchorRef = useRef<SVGCircleElement | null>(null)

  useEffect(() => {
    if (!map || !center || !visible) return
    const toScreen = (pos: [number, number]): [number, number] => {
      const ll = CoordinateService.scenePositionToLngLat([pos[0], 0, pos[1]], center)
      const p = map.project(ll as [number, number])
      return [p.x, p.y]
    }
    const setLine = (el: SVGLineElement | null | undefined, a: [number, number], b: [number, number]) => {
      if (!el) return
      const [ax, ay] = toScreen(a)
      const [bx, by] = toScreen(b)
      el.setAttribute('x1', String(ax))
      el.setAttribute('y1', String(ay))
      el.setAttribute('x2', String(bx))
      el.setAttribute('y2', String(by))
    }
    const update = () => {
      for (const e of edges) setLine(edgeRefs.current.get(e.id), e.a, e.b)
      for (const n of nodes) {
        const el = nodeRefs.current.get(n.id)
        if (!el) continue
        const [x, y] = toScreen(n.pos)
        el.setAttribute('cx', String(x))
        el.setAttribute('cy', String(y))
      }
      for (const l of rwyLines) setLine(rwyLineRefs.current.get(l.id), l.a, l.b)
      for (const a of rwyArrows) setLine(rwyArrowRefs.current.get(a.key), a.a, a.b)
      for (const h of rwyHandles) {
        const el = rwyHandleRefs.current.get(h.key)
        if (!el) continue
        const [x, y] = toScreen(h.pos)
        el.setAttribute('cx', String(x))
        el.setAttribute('cy', String(y))
      }
      for (const lb of rwyLabels) {
        const el = rwyLabelRefs.current.get(lb.key)
        if (!el) continue
        const [x, y] = toScreen(lb.pos)
        el.setAttribute('x', String(x))
        el.setAttribute('y', String(y - 10))
      }
      if (anchorRef.current && runwayAnchorPos) {
        const [x, y] = toScreen(runwayAnchorPos)
        anchorRef.current.setAttribute('cx', String(x))
        anchorRef.current.setAttribute('cy', String(y))
      }
    }
    let raf = 0
    const schedule = () => {
      if (raf) return
      raf = requestAnimationFrame(() => {
        raf = 0
        update()
      })
    }
    map.on('move', schedule)
    update()
    return () => {
      map.off('move', schedule)
      if (raf) cancelAnimationFrame(raf)
    }
  }, [map, center, visible, nodes, edges, rwyLines, rwyHandles, rwyArrows, rwyLabels, runwayAnchorPos])

  if (!visible || !plan) return null

  return (
    <svg className="pointer-events-none absolute inset-0 z-[7] h-full w-full overflow-visible">
      <defs>
        <marker id="rwy-arrow-land" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#34d399" />
        </marker>
        <marker id="rwy-arrow-tko" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill="#fbbf24" />
        </marker>
      </defs>

      {/* Runways (drawn under the node/edge network) */}
      {rwyLines.map((l) => (
        <line
          key={l.id}
          ref={(el) => rwyLineRefs.current.set(l.id, el)}
          stroke={l.selected ? '#ffffff' : l.active ? '#cbd5e1' : '#64748b'}
          strokeWidth={l.selected ? 9 : 7}
          strokeOpacity={l.active ? 0.55 : 0.25}
          strokeLinecap="butt"
        />
      ))}
      {rwyArrows.map((a) => (
        <line
          key={a.key}
          ref={(el) => rwyArrowRefs.current.set(a.key, el)}
          stroke={a.color}
          strokeWidth={2.5}
          strokeOpacity={0.95}
          markerEnd={`url(#${a.color === '#34d399' ? 'rwy-arrow-land' : 'rwy-arrow-tko'})`}
        />
      ))}
      {rwyHandles.map((h) => (
        <circle
          key={h.key}
          ref={(el) => rwyHandleRefs.current.set(h.key, el)}
          r={6}
          fill="#0b1220"
          stroke={h.selected ? '#ffffff' : '#cbd5e1'}
          strokeWidth={h.selected ? 3 : 2}
        />
      ))}
      {rwyLabels.map((lb) => (
        <text
          key={lb.key}
          ref={(el) => rwyLabelRefs.current.set(lb.key, el)}
          fill="#e2e8f0"
          fontSize={12}
          fontWeight={700}
          textAnchor="middle"
          style={{ paintOrder: 'stroke', stroke: '#0b1220', strokeWidth: 3 }}
        >
          {lb.text}
        </text>
      ))}
      {runwayAnchorPos && <circle ref={anchorRef} r={6} fill="none" stroke="#38bdf8" strokeWidth={2} strokeDasharray="3 3" />}

      {edges.map((e) => (
        <line
          key={e.id}
          ref={(el) => edgeRefs.current.set(e.id, el)}
          stroke={e.color}
          strokeWidth={e.selected ? 4 : 2.25}
          strokeOpacity={e.active ? (e.selected ? 1 : 0.85) : 0.25}
          strokeDasharray={!e.active || e.temporary ? '5 5' : undefined}
          strokeLinecap="round"
        />
      ))}
      {nodes.map((n) => (
        <circle
          key={n.id}
          ref={(el) => nodeRefs.current.set(n.id, el)}
          r={n.isExit ? 6 : 5}
          fill={n.color}
          fillOpacity={n.active ? 0.95 : 0.3}
          stroke={n.selected ? '#ffffff' : n.anchor ? '#38bdf8' : '#0b1220'}
          strokeWidth={n.selected || n.anchor ? 3 : 1.25}
        />
      ))}
    </svg>
  )
}
